import { useQuery } from '@tanstack/react-query'
import { useAlertService } from '@/shared/lib/useAlertService'
import { CheckinsService } from '@/shared/api/rest/services/checkins.service'
import {
    adaptFindAllCheckins,
    FindAllCheckinsResult
} from '@/shared/api/rest/checkins.adapters'
import convertDateToIsoString from '@/shared/utils/convertDateToIsoString'

export type CheckinResultType = {
    checkInResults: FindAllCheckinsResult['checkInResults']
    checkInAggregates: {
        totalCheckedIn: number
        totalCheckedOut: number
        totalStillCheckedIn: number
    }
}

export type CheckinStatusFilter = '' | 'in' | 'out'

type Param = {
    startDate: Date
    branchId: string
    page?: number
    limit?: number
    ageGroup?: string
    status?: CheckinStatusFilter
}

/**
 * useFindAllCheckins - fetch all checkin data for the reports/logs view.
 * @returns {data: CheckinResultType, loading: boolean}
 */
export function useFindAllCheckins({
    startDate,
    branchId,
    page = 1,
    limit = 1000,
    ageGroup = '',
    status = ''
}: Param) {
    const alertService = useAlertService()
    const isoDate = convertDateToIsoString(startDate)

    const empty: CheckinResultType = {
        checkInResults: [],
        checkInAggregates: {
            totalCheckedIn: 0,
            totalCheckedOut: 0,
            totalStillCheckedIn: 0
        }
    }

    const { data, isLoading } = useQuery<FindAllCheckinsResult>({
        queryKey: ['find-all-checkins', branchId, isoDate, page, limit, ageGroup, status],
        queryFn: async () => {
            try {
                const raw = await CheckinsService.list({
                    branch: branchId,
                    checkedInDate: isoDate,
                    ageGroup: ageGroup || undefined,
                    checkedOut:
                        status === 'in' ? 'false' : status === 'out' ? 'true' : undefined,
                    page,
                    limit,
                    sortBy: 'checkedInDate',
                    sortOrder: 'DESC'
                })
                return adaptFindAllCheckins(raw)
            } catch (error) {
                alertService.error(
                    error instanceof Error ? error.message : 'Failed to load checkins'
                )
                return adaptFindAllCheckins({})
            }
        }
    })

    return {
        loading: isLoading,
        data: (data as CheckinResultType) ?? empty
    }
}
